// SummaryIcon.js
import React, { useState, useEffect } from 'react';
import { Anthropic } from "@anthropic-ai/sdk";
import { decryptData } from "../encryptionUtils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FreeTrialManager from './FreeTrialManager';
import './SummaryIcon.css';

function SummaryIcon({ topic }) {
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTopicId, setCurrentTopicId] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false); // State to control tooltip visibility
  const [tooltipTimer, setTooltipTimer] = useState(null); // Timer to prevent flickering

  // Reset summary when topic changes
  useEffect(() => {
    if (topic?.id !== currentTopicId) {
      setSummary('');
      setError(null);
      setCurrentTopicId(topic?.id);
    }
  }, [topic?.id, currentTopicId]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
      }
    };
  }, [tooltipTimer]);

  const handleMouseEnter = () => {
    // Clear any existing timer
    if (tooltipTimer) {
      clearTimeout(tooltipTimer);
      setTooltipTimer(null);
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    // Set a small delay before hiding to prevent flickering
    const timer = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
    setTooltipTimer(timer);
  };

  // Get API key for request (from trial or user-provided)
  const getApiKeyForRequest = () => {
    // First check if trial is active
    if (FreeTrialManager.isTrialActive()) {
      const trialKey = FreeTrialManager.getTrialApiKey();
      console.log("SummaryIcon: Using trial API key:", !!trialKey);
      return trialKey;
    } else {
      // Get user-provided key
      const userKey = localStorage.getItem('anthropic_api_key') || 
        (localStorage.getItem('anthropic_api_key_encrypted') ? 
          decryptData(localStorage.getItem('anthropic_api_key_encrypted')) : '');
      console.log("SummaryIcon: Using user API key:", !!userKey);
      return userKey;
    }
  };

  const generateSummary = async () => {
    if (!topic || !topic.messages || topic.messages.length < 2) {
      setError("Not enough content to summarize.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Get API key (use trial key if available)
      const apiKey = getApiKeyForRequest();
      
      if (!apiKey) {
        throw new Error("API key is required for summary generation");
      }
      
      // Create a client for this specific request
      const client = new Anthropic({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
      });
      
      // Create a simplified version of the conversation for the summary
      // Ensure all messages are properly converted to strings
      const conversationText = topic.messages.map(msg => {
        // Safely convert message content to string
        const content = typeof msg.content === 'string' ? msg.content : 
                        msg.content ? JSON.stringify(msg.content) : '';
        return `${msg.role === 'user' ? 'Human' : 'Assistant'}: ${content}`;
      }).join('\n\n');
      
      const promptText = `Generate a structured summary of no more than 220 words of this discussion thread, capturing its primary purpose, key activities, and artifacts created. The summary should include:
    1. Primary Purpose: Clearly define the main objectives and context of the discussion.
    2. Activities: Summarize the key actions or steps taken so far.
    3. Artifacts Created: List any outputs generated in the discussion, like programs, essays, and analysis.
    4. Conclusion & Next Steps: Depending on the thread, suggest next steps (especially things they can do in the current thread), further learnings or a conclusion.
    
    Be careful not to include raw JavaScript objects in your output. If you see something that looks like an object (e.g., [object Object]), convert it to a meaningful text description instead.`;
      
      const response = await client.messages.create({
        model: localStorage.getItem('anthropic_model') || 'claude-3-7-sonnet-20250219',
        max_tokens: 1024,
        messages: [
          {
            role: 'user',
            content: `Here is a conversation thread. ${promptText}\n\nCONVERSATION:\n${conversationText}`
          }
        ],
        temperature: 0.3
      });
      
      if (response?.content) {
        const summaryText = response.content[0].text;
        // Search for and fix any [object Object] patterns
        const cleanedSummary = summaryText.replace(/\[object Object\]/g, "item");
        setSummary(cleanedSummary);
        
        // If we're using the trial, record prompt usage
        if (FreeTrialManager.isTrialActive()) {
          FreeTrialManager.recordSuccessfulPrompt().then(() => {
            // Dispatch event to update UI for trial state changes
            window.dispatchEvent(new Event('trialUpdated'));
          }).catch(err => {
            console.error("Error recording summary prompt usage:", err);
          });
        }
      } else {
        throw new Error('Invalid summary response');
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      setError("Failed to generate summary: " + (error.message || "Unknown error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    setShowSummary(true);
    if (!summary && !isLoading) {
      generateSummary();
    }
  };

  const handleClose = () => {
    setShowSummary(false);
  };

  const handleCopySummary = () => {
    // Create a temporary element to hold the HTML
    const tempElement = document.createElement('div');
    
    // Render the markdown to HTML
    tempElement.innerHTML = summary;
    
    // Extract text content from HTML
    const textContent = tempElement.textContent || tempElement.innerText || summary;
    
    // Copy to clipboard
    navigator.clipboard.writeText(textContent);
    
    // Could add copied indicator here
    const copyButton = document.querySelector('.copy-summary-button');
    if (copyButton) {
      const originalText = copyButton.textContent;
      copyButton.textContent = 'Copied!';
      setTimeout(() => {
        copyButton.textContent = originalText;
      }, 2000);
    }
  };

  // Custom renderer components for more compact rendering
  // Fixed to properly pass children and avoid ESLint warnings
  const renderers = {
    // Make headings more compact
    h1: ({node, children, ...props}) => <h1 style={{marginTop: '8px', marginBottom: '4px'}} {...props}>{children}</h1>,
    h2: ({node, children, ...props}) => <h2 style={{marginTop: '8px', marginBottom: '4px'}} {...props}>{children}</h2>,
    h3: ({node, children, ...props}) => <h3 style={{marginTop: '6px', marginBottom: '3px'}} {...props}>{children}</h3>,
    // Make paragraphs more compact
    p: ({node, children, ...props}) => <p style={{marginTop: '3px', marginBottom: '3px'}} {...props}>{children}</p>,
    // Make lists more compact
    ul: ({node, children, ...props}) => <ul style={{marginTop: '3px', marginBottom: '3px', paddingLeft: '20px'}} {...props}>{children}</ul>,
    ol: ({node, children, ...props}) => <ol style={{marginTop: '3px', marginBottom: '3px', paddingLeft: '20px'}} {...props}>{children}</ol>,
    // Make list items more compact
    li: ({node, children, ...props}) => <li style={{marginBottom: '2px'}} {...props}>{children}</li>
  };

  return (
    <>
      <div className="summary-button-container">
        <button 
          className="summary-icon-button" 
          onClick={handleClick}
          aria-label="Generate conversation summary"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <i>i</i>
        </button>
        {showTooltip && (
          <div className="simple-tooltip">Conversation Summary</div>
        )}
      </div>
      
      {showSummary && (
        <div className="summary-overlay" onClick={(e) => {
          if (e.target.className === 'summary-overlay') {
            handleClose();
          }
        }}>
          <div className="summary-container">
            <div className="summary-header">
              <h3>Conversation Summary</h3>
              <button 
                className="close-button" 
                onClick={handleClose}
                aria-label="Close summary"
              >
                ×
              </button>
            </div>
            
            <div className="summary-content">
              {isLoading ? (
                <div className="summary-loading">
                  <div className="summary-spinner" role="progressbar" />
                  <p>Generating summary...</p>
                </div>
              ) : error ? (
                <div className="summary-error">
                  <p>{error}</p>
                </div>
              ) : (
                <div className="summary-text">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={renderers}
                  >
                    {summary}
                  </ReactMarkdown>
                </div>
              )}
            </div>
            
            <div className="summary-footer">
              <button 
                className="copy-summary-button" 
                onClick={handleCopySummary}
                disabled={!summary || isLoading}
              >
                Copy Summary
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SummaryIcon;